define('services/componentLoader',[
    'infoArchitecture/siteMap',
    'jquery',
    'lib/bluebird',
    'lib/knockout',
    'lib/lodash4',
    'require',
    'routes',
    'services/featureToggleService',
    'services/loggingService',
    'util/queryParamParser',
    'viewModels/primaryNav',
    'services/componentViewService'
], function (
    siteMap,
    $,
    Promise,
    ko,
    _,
    require,
    routes,
    featureToggleService,
    loggingService,
    queryParser,
    primaryNav,
    componentViewService
) {
    'use strict';

    function pRequire(dependency) {
        return new Promise(function (resolve, reject) {
            require([dependency], resolve, reject);
        });
    }

    /*
    This auth check is not working correctly with parameters in url.
    Fixing is not necessary since API will check permissions. Any attempted fix
    should handle conflicting permissions, and not require the /:id page's permissions on the /add page.
    */
    function translateComponentPath(path) {
        if(path.charAt(0) === '#'){
            return path;
        }
        return '#' + path.replace(/^\//, '');
    }

    function findBadRoutes(componentName, routes, navParent) {
        var validRouteRegex = navParent ? new RegExp('^(' + componentName + '|' + navParent + ')(?:/|$)') : new RegExp('^' + componentName + '(?:/|$)');
        return _.reject(routes, function (route) {
            return validRouteRegex.test(route);
        });
    }

    function fetchComponentStrings(componentDefinition) {
        var locale = ko.i18n.getLocale();
        var stringsPath = 'json!' + componentDefinition.stringsPath + '/' + locale + '.json';
        return pRequire(stringsPath)
            .catch(function () {
                return {};  //always return something - we'll fall back to en-US in this case
            });
    }

    function fetchFallbackStrings(componentDefinition) {
        var locale = ko.i18n.getLocale();
        if (locale !== 'en-US') {
            var stringsPath = 'json!' + componentDefinition.stringsPath + '/en-US.json';
            return pRequire(stringsPath);
        } else {
            return Promise.resolve({});
        }
    }

    function validateStrings(componentName, strings) {
        var locale = ko.i18n.getLocale();
        var validKeyRegex = new RegExp('^' + componentName + '\\.');
        var invalidKeys = _.reject(_.keys(strings), function (key) {
            return validKeyRegex.test(key);
        });

        if (invalidKeys.length > 0) {
            throw new Error('Found invalid prefixes in ' + locale + ' strings file for ' + componentName + ': ' + invalidKeys.join(', '));
        }
    }

    function validateRootView(componentName, rootView) {
        if (!_.isFunction(rootView.dispose)) {
            throw new Error('No dispose method on root view for ' + componentName + ' component.');
        }
    }

    function validateRouteDefinitions(componentName, routes, navParent) {
        var badRoutes = findBadRoutes(componentName, _.keys(routes), navParent);
        if (badRoutes.length > 0) {
            throw new Error('Bad route definitions in ' + componentName + ': ' + badRoutes.join(', '));
        }
    }

    function validateRouteAuthorizations(componentName, routeAuthorizations, navParent) {
        var badRoutes = findBadRoutes(componentName, _.map(routeAuthorizations, 'path'), navParent);
        if (badRoutes.length > 0) {
            throw new Error('Bad route authorizations in ' + componentName + ': ' + badRoutes.join(', '));
        }
    }

    function validateSiteMap(componentName, siteMap) {
        var keys = _.keys(siteMap);
        if (keys.length > 1 || keys[0] !== componentName) {
            throw new Error('Bad siteMap definition in ' + componentName + ': ' + keys.join(', '));
        }
    }

    function registerRoutes(componentName, componentDefinition) {
        var routeDefinitions = componentDefinition.routeInfo.routes;
        routeDefinitions = _.reduce(componentDefinition.routeInfo.routes, function (routes, routeDetails, path) {
            var newPath = translateComponentPath(path);
            routes[newPath] = routeDetails;
            return routes;
        }, {});

        var routeAuthorizations = _.map(componentDefinition.routeInfo.routeAuthorizations, function (authorization) {
            //TODO Clean up core route logic to make this translation unnecessary.
            return {
                path: translateComponentPath(authorization.path) + '(?:$|\\/.*)',
                roles: authorization.authorizations
            };
        });

        routes.addAuthorizationChecks(routeAuthorizations);
        routes.registerComponentRoutes(componentName, componentDefinition.routeContext, componentDefinition.rootView, routeDefinitions);
    }

    function registerSiteMap(componentName, componentDefinition) {
        if (componentDefinition.navParent) {
            siteMap.registerEntries(componentDefinition.componentMap, componentName, componentDefinition.navParent);
        } else {
            siteMap.registerEntries(componentDefinition.componentMap, componentName);
        }
    }


    function registerNavEntries(componentName, navEntries, navParent) {
        _.each(navEntries, function (entry) {
            if (entry.href) {
                entry.href = translateComponentPath(entry.href);
            }
        });

        if (navParent) {
            primaryNav.registerComponentEntries(componentName, navEntries, navParent);
        } else {
            primaryNav.registerComponentEntries(componentName, navEntries);
        }
    }

    function createComponentNav(componentName, componentDefinition) {
        // If the navEntries are a computed, create a computed to update primaryNav if the values change
        if (ko.isComputed(componentDefinition.navEntries)) {
            ko.computed(function() {
                var entries = componentDefinition.navEntries();

                if (entries.length > 0) {
                    registerNavEntries(componentName, entries, componentDefinition.navParent);
                }
            });
        }
        else if (componentDefinition.navEntries) {
            registerNavEntries(componentName, componentDefinition.navEntries, componentDefinition.navParent);
        }
    }

    function applyStyles(componentDefinition) {
        if (componentDefinition.cssUrl) {
            $('<link>')
                .attr('href', componentDefinition.cssUrl)
                .attr('type', 'text/css')
                .attr('rel', 'stylesheet')
                .appendTo('head');
        }
    }

    /**
     * Checks that the component with the given name and component definition meets the feature
     * requirements given in the component definition's "requiresFeature" field (if present).
     * @param componentName the name of the component
     * @param componentDefinition the component definition
     * @returns Promise<boolean> indicating whether the feature requirement is met for the component
     */
    function checkFeatureRequirement(componentName, componentDefinition) {
        var requiredFeature = componentDefinition && componentDefinition.requiresFeature;
        var requiredFeatureState = true;
        if (requiredFeature && _.startsWith(requiredFeature, '!')) {
            requiredFeature = requiredFeature.slice(1);
            requiredFeatureState = false;
        }
        if (!requiredFeature) {
            loggingService.trace('Skipping feature check because component definition has no requiredFeature key', { componentName: componentName });
            return Promise.resolve(true);
        }
        return featureToggleService.getFeatureToggle(requiredFeature).then(function(featureState){
            loggingService.debug('Checking feature requirement for component', {
                componentName: componentName,
                requiredFeatureState: requiredFeature + '=' + requiredFeatureState,
                currentFeatureState: requiredFeature + '=' + featureState
            });
            return featureState === requiredFeatureState;
        }, function(error) {
            loggingService.error('Failed to load feature toggle state for component', {
                componentName: componentName,
                featureToggle: requiredFeature,
                error: error
            });
            return false;
        });
    }

    function registerComponent(componentName, componentDefinition) {
        return checkFeatureRequirement(componentName, componentDefinition).then(function(featureRequirementMet){
            if (!featureRequirementMet) {
                loggingService.info('Component registration skipped due to unmet feature requirement', {
                    componentName: componentName,
                    requiredFeature: componentDefinition.requiresFeature
                });
                return;
            }
            loggingService.debug('Registering component', {
                componentName: componentName,
                componentDefinition: componentDefinition
            });

            return Promise.all([
                fetchComponentStrings(componentDefinition),
                fetchFallbackStrings(componentDefinition)
            ]).then(function (strings) {
                var currentLocaleStrings = strings[0];
                var usLocaleStrings = strings[1];
                validateStrings(componentName, currentLocaleStrings);
                validateStrings(componentName, usLocaleStrings);
                validateSiteMap(componentName, componentDefinition.componentMap);
                if (componentDefinition.componentVersion >= 2) {
                    validateRootView(componentName, componentDefinition.rootView);
                }

                validateRouteDefinitions(componentName, componentDefinition.routeInfo.routes, componentDefinition.navParent);
                validateRouteAuthorizations(componentName, componentDefinition.routeInfo.routeAuthorizations, componentDefinition.navParent);

                if (componentDefinition.sharedViews) {
                    loggingService.debug('Storing shared views for component', { componentName: componentName });
                    componentViewService.setSharedViewsForComponent(componentName, componentDefinition.sharedViews);
                }

                ko.i18n.registerStrings(usLocaleStrings, 'en-US');
                ko.i18n.registerStrings(currentLocaleStrings);
                registerSiteMap(componentName, componentDefinition);
                registerRoutes(componentName, componentDefinition);
                createComponentNav(componentName, componentDefinition);
                applyStyles(componentDefinition);
            });
        });
    }

    return {
        registerComponents: function (componentsConfig) {
            var promises = _.map(componentsConfig, function (jsUrl, componentName) {
                loggingService.debug('Loading component', { componentName: componentName });
                var componentVersion = queryParser.getParameterValue(componentName + '-component-version');
                if (componentVersion) {
                    var urlSegments = jsUrl.split('/');
                    urlSegments.splice(-1, 0, componentVersion);
                    jsUrl = urlSegments.join('/');
                }
                // If pathname looks like "/contact-center/<engage version>/embedded.html" then we're
                // a non-mainline (i.e. Time Machine) version of Engage. Component definition URLs
                // need to be relative to base /contact-center path to work, i.e. we want
                //    /contact-center/<component path>
                // and not
                //    /contact-center/<engage version>/<component path>
                // which won't exist.
                if (/\/contact-center\/([^/]+)\/(embedded|frameless|index)\.html/.test(window.location.pathname)) {
                    // Adjust component URL when loading a specific
                    // build of the Engage core when deployed
                    jsUrl = '/contact-center/' + jsUrl;
                }
                return pRequire(jsUrl)
                    .then(function (componentDefinition) {
                        return registerComponent(componentName, componentDefinition);
                    })
                    .catch(function (error) {
                        loggingService.error('Error loading component', {
                            componentName: componentName,
                            componentUrl: jsUrl,
                            error: error
                        });
                    });
            });

            return Promise.all(promises);
        }
    };
});

